import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { useSiteConfig, pickI18n } from '../contexts/SiteConfigContext';
import {
  Home,
  Sparkles,
  Tag,
  Image as ImageIcon,
  ShoppingBag,
  Phone,
  Calendar,
} from 'lucide-react';

/**
 * QuickNav — UI-only section that renders large, pink, rounded buttons
 * for every visible nav entry from the CMS (config.nav) PLUS a booking CTA.
 *
 * IMPORTANT:
 *  - Labels & routes come from the CMS (`config.nav` + i18n).
 *  - Icons & colors are purely visual (defined here).
 *  - No services/products/gallery content is hardcoded.
 */

// Visual-only icon registry, keyed by nav item `key` from CMS.
// If the CMS adds a new key not listed here, we fall back to the Sparkles icon.
const ICON_BY_KEY = {
  home: Home,
  services: Sparkles,
  pricing: Tag,
  gallery: ImageIcon,
  products: ShoppingBag,
  contact: Phone,
  booking: Calendar,
};

const BOOKING_LABELS = {
  de: 'Termin buchen',
  en: 'Book Appointment',
  ar: 'حجز موعد',
};

const SECTION_TITLE = {
  de: 'Schnellzugriff',
  en: 'Quick Access',
  ar: 'وصول سريع',
};

const SECTION_SUBTITLE = {
  de: 'Entdecken Sie alles, was Dalaa Beauty zu bieten hat',
  en: 'Explore everything Dalaa Beauty has to offer',
  ar: 'اكتشف كل ما يقدمه دلع بيوتي',
};

const QuickNav = () => {
  const { lang } = useLang();
  const { config } = useSiteConfig();
  const colors = config.identity?.colors || {};
  const primary = colors.primary || '#c4477a';
  const primaryDark = colors.primary_dark || '#a83668';
  const textDark = colors.text_dark || '#5a1d3b';

  const navItems = (config.nav || [])
    .filter((n) => n.visible !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // Build the final button list: nav from CMS + a booking CTA at the end.
  const items = [
    ...navItems.map((n) => ({
      key: n.key,
      to: n.to,
      label: pickI18n(n.label, lang),
      Icon: ICON_BY_KEY[n.key] || Sparkles,
      featured: false,
    })),
    {
      key: 'booking',
      to: '/booking',
      label: BOOKING_LABELS[lang] || BOOKING_LABELS.de,
      Icon: Calendar,
      featured: true,
    },
  ];

  return (
    <section
      className="relative py-20 lg:py-28"
      data-testid="home-quicknav-section"
      style={{
        background:
          'linear-gradient(180deg, #ffffff 0%, #fdf5f7 60%, #fbe6ee 100%)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12 lg:mb-16">
          <p
            className="text-xs md:text-sm tracking-[0.3em] uppercase mb-3"
            style={{ color: colors.accent || '#b54e7d' }}
          >
            Dalaa Beauty
          </p>
          <h2
            className="font-display text-3xl sm:text-4xl lg:text-5xl"
            style={{ color: textDark, fontWeight: 500 }}
          >
            {SECTION_TITLE[lang] || SECTION_TITLE.de}
          </h2>
          <p className="mt-3 text-sm md:text-base text-gray-600 max-w-xl mx-auto">
            {SECTION_SUBTITLE[lang] || SECTION_SUBTITLE.de}
          </p>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6"
          data-testid="home-quicknav-grid"
        >
          {items.map(({ key, to, label, Icon, featured }) => (
            <Link
              key={key}
              to={to}
              data-testid={`home-quicknav-${key}`}
              className="group relative flex flex-col items-center justify-center text-center
                         rounded-3xl px-4 py-7 sm:py-8
                         min-h-[140px] sm:min-h-[160px]
                         transition-all duration-300
                         shadow-[0_4px_20px_-8px_rgba(196,71,122,0.25)]
                         hover:shadow-[0_12px_32px_-10px_rgba(196,71,122,0.45)]
                         hover:-translate-y-1
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={
                featured
                  ? {
                      background: `linear-gradient(135deg, ${primary} 0%, ${primaryDark} 100%)`,
                      color: '#ffffff',
                    }
                  : {
                      backgroundColor: '#ffffff',
                      color: textDark,
                      border: '1px solid #fbd5e2',
                    }
              }
            >
              <span
                className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full mb-3 transition-transform duration-300 group-hover:scale-110"
                style={
                  featured
                    ? { backgroundColor: 'rgba(255,255,255,0.18)' }
                    : { backgroundColor: '#fdf2f5' }
                }
              >
                <Icon
                  className="w-6 h-6 sm:w-7 sm:h-7"
                  strokeWidth={1.6}
                  style={{ color: featured ? '#ffffff' : primary }}
                />
              </span>
              <span
                className="text-sm sm:text-base font-medium tracking-wide"
                style={{ color: featured ? '#ffffff' : textDark }}
              >
                {label}
              </span>
              {featured && (
                <span className="mt-1 text-[10px] uppercase tracking-[0.25em] opacity-90">
                  {lang === 'ar' ? 'احجز الآن' : lang === 'en' ? 'Book now' : 'Jetzt buchen'}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default QuickNav;
